import { useState, useEffect } from "react";
import {
  Filter,
  Calendar,
  Mail,
  Activity,
  X,
  RefreshCw,
} from "lucide-react";
import {
  AuditTrailListParams,
  EventType,
} from "../../services/auditTrail.service";
import Tooltip from "../../widgets/ToolTip";

interface AuditTrailFiltersProps {
  onFiltersChange: (filters: AuditTrailListParams) => void;
  eventTypes: EventType[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

export default function AuditTrailFilters({
  onFiltersChange,
  eventTypes,
  isLoading = false,
  onRefresh,
}: AuditTrailFiltersProps) {
  const [filters, setFilters] = useState<AuditTrailListParams>({
    skip: 0,
    limit: 10,
  });


  // Helper function to format date for input
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Set default date range (last 30 days)
  useEffect(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    setFilters((prev) => ({
      ...prev,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    }));
  }, []);

  const handleFilterChange = (key: keyof AuditTrailListParams, value: any) => {
    let processedValue = value;

    // Convert date strings to ISO format for API
    if ((key === "start_date" || key === "end_date") && value) {
      const date = new Date(value);
      if (key === "start_date") {
        date.setHours(0, 0, 0, 0);
      } else {
        date.setHours(23, 59, 59, 999);
      }
      processedValue = date.toISOString();
    }

    const newFilters = {
      ...filters,
      [key]: processedValue || undefined,
      skip: 0, // Reset pagination when filters change
    };

    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: AuditTrailListParams = {
      skip: 0,
      limit: 10,
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = () => {
    return !!(
      filters.user_id ||
      filters.event_type ||
      filters.action_by ||
      filters.email ||
      filters.start_date ||
      filters.end_date
    );
  };

  const getDateValueForInput = (isoDate?: string) => {
    if (!isoDate) return "";
    return formatDateForInput(new Date(isoDate));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            {hasActiveFilters() && (
              <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Tooltip content="Refresh data">
              <button
                onClick={onRefresh}
                disabled={isLoading}
                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition disabled:opacity-50"
                type="button"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                />
              </button>
                </Tooltip>
            )}
            {hasActiveFilters() && (
              <Tooltip content="Clear filters">
              <button
                onClick={clearFilters}
                className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
              </Tooltip>
              
            )}
          </div>
        </div>

        {/* Basic Filters - Always Visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
            </label>
            <div className="relative">
              <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={filters.event_type || ""}
                onChange={(e) =>
                  handleFilterChange("event_type", e.target.value)
                }
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">
                  All Event Types ({eventTypes.length} available)
                </option>

                {/* Group events by category */}
                <optgroup label="👤 User Management">
                  {eventTypes
                    .filter(
                      (et) =>
                        et.value.includes("USER_") &&
                        !et.value.includes("GROUP")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>

                <optgroup label="👥 Groups & Roles">
                  {eventTypes
                    .filter(
                      (et) =>
                        et.value.includes("GROUP_") ||
                        et.value.includes("ROLE_") ||
                        et.value.includes("PERMISSION_")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>

                <optgroup label="🔐 Authentication">
                  {eventTypes
                    .filter(
                      (et) =>
                        et.value.includes("LOGIN") ||
                        et.value.includes("LOGOUT") ||
                        et.value.includes("SOCIAL")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>

                <optgroup label="📁 Files & Storage">
                  {eventTypes
                    .filter(
                      (et) =>
                        et.value.includes("FILE_") ||
                        et.value.includes("STORAGE_") ||
                        et.value.includes("NAMESPACE_")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>

                <optgroup label="⚙️ Settings & Configuration">
                  {eventTypes
                    .filter(
                      (et) =>
                        et.value.includes("SETTINGS_") ||
                        et.value.includes("DATABASE_CONFIG_") ||
                        et.value.includes("WEBHOOK_CONFIG_")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>

                <optgroup label="🔧 Applications & Workflows">
                  {eventTypes
                    .filter(
                      (et) =>
                        et.value.includes("APP_") ||
                        et.value.includes("WORKFLOW_")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>

                <optgroup label="🔔 Other Events">
                  {eventTypes
                    .filter(
                      (et) =>
                        !et.value.includes("USER_") &&
                        !et.value.includes("GROUP_") &&
                        !et.value.includes("ROLE_") &&
                        !et.value.includes("PERMISSION_") &&
                        !et.value.includes("LOGIN") &&
                        !et.value.includes("LOGOUT") &&
                        !et.value.includes("SOCIAL") &&
                        !et.value.includes("FILE_") &&
                        !et.value.includes("STORAGE_") &&
                        !et.value.includes("NAMESPACE_") &&
                        !et.value.includes("SETTINGS_") &&
                        !et.value.includes("DATABASE_CONFIG_") &&
                        !et.value.includes("WEBHOOK_CONFIG_") &&
                        !et.value.includes("APP_") &&
                        !et.value.includes("WORKFLOW_")
                    )
                    .map((eventType) => (
                      <option key={eventType.value} value={eventType.value}>
                        {eventType.label}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="date"
                value={getDateValueForInput(filters.start_date)}
                onChange={(e) =>
                  handleFilterChange("start_date", e.target.value)
                }
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="date"
                value={getDateValueForInput(filters.end_date)}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email"
                placeholder="Search by email..."
                value={filters.email || ""}
                onChange={(e) => handleFilterChange("email", e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
